import {
	AiAssistantUsageModel,
	CreditBucketModel,
	getAiAssistantUsage
} from "client/Api/AIAssistantSettings/aIAssistantSettings";
import {
	Anchor,
	BusyIndicator,
	FormattedText,
	NoResults
} from "client/Components";
import { ProgressBar } from "client/Components/ProgressBar/ProgressBar";
import {
	INFINITE_FRACTION_DIGITS,
	NumberFormatter,
	useCurrencyFormatter,
	useForceRerender,
	useNumberFormatter
} from "client/Hooks";
import {
	clamp,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { Guid } from "client_legacy/ndaw/guid";
import { never } from "client_legacy/ndaw/errors";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	ReactNode,
	useEffect,
	useState
} from "react";
import styles from "./UsageSection.module.less";

export type UsageSectionProps = {
	kellyHelpsUrl: string;
	kellyTenantId: Guid;
};

export const UsageSection = (props: UsageSectionProps): ReactElement => {
	const {
		kellyHelpsUrl,
		kellyTenantId
	} = props;

	const [state, setState] = useState<UsageState>({ kind: "loading" });
	const [reload, usageVersion] = useForceRerender();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchUsage(signal: AbortSignal): Promise<void> {
				setState({ kind: "loading" });

				try {
					const usage: AiAssistantUsageModel = await getAiAssistantUsage(signal);
					setState({ kind: "loaded", usage });
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setState({ kind: "failed" });
				}
			}

			const cancellation = new AbortController();
			fetchUsage(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[usageVersion]
	);

	const renderContent = (): ReactElement => {
		switch (state.kind) {
			case "loading":
				return (
					<div className={styles.loading}>
						<BusyIndicator />
					</div>
				);
			case "loaded":
				return (
					<UsageCards usage={state.usage} />
				);
			case "failed":
				return (
					<NoResults
						icon="gear"
						title={localize("_ErrorLoadingTheData")}
						message={localize("_ErrorDuringDataRetrieval")}
						onRetry={reload}
						trackingId="usage-error"
					/>
				);
			default:
				return never(state);
		}
	};

	return (
		<div className={styles.root}>
			{renderContent()}
			<Anchor
				href={getKellyHelpsSignInUrl(kellyHelpsUrl, kellyTenantId)}
				target="_blank"
				trackingId="kelly-helps-link"
			>
				{localize("_OpenKellyHelps")}
			</Anchor>
		</div>
	);
};
UsageSection.displayName = "UsageSection";

type UsageState = {
	kind: "loading";
} | {
	kind: "loaded";
	usage: AiAssistantUsageModel;
} | {
	kind: "failed";
};

type UsageCardsProps = {
	usage: AiAssistantUsageModel;
};

const UsageCards = (props: UsageCardsProps): ReactElement => {
	const {
		subscription,
		subscriptionCredits,
		freeCredits,
		extraCredits
	} = props.usage;

	const formatNumber: NumberFormatter = useNumberFormatter({
		culture: currentCulture,
		format: "thousand-separator"
	});

	const formatEuro: NumberFormatter = useCurrencyFormatter({
		culture: currentCulture,
		currency: "EUR"
	});

	const formatCreditPrice: NumberFormatter = useCurrencyFormatter({
		culture: currentCulture,
		currency: "EUR",
		maximumFractionDigits: INFINITE_FRACTION_DIGITS
	});

	return (
		<ul className={styles.cards}>
			<li>
				<UsageCard
					name={localize("_Subscription")}
					amount={formatEuro(subscription.monthlyPriceEur)}
					used={formatUsed(formatNumber, subscriptionCredits)}
					trackingId="subscription-credits-used"
					usedPercentage={getUsedPercentage(subscriptionCredits)}
				/>
			</li>
			<li>
				<UsageCard
					name={localize("_(Cost)Free")}
					used={formatUsed(formatNumber, freeCredits)}
					trackingId="free-credits-used"
					usedPercentage={getUsedPercentage(freeCredits)}
				/>
			</li>
			<li>
				<UsageCard
					name={localize("_(Cost)Extra")}
					amount={formatEuro(extraCredits.costEur)}
					used={formatNumber(extraCredits.used)}
					note={(
						<FormattedText
							text={localize("_PricePerCredit")}
							thingsToReplace={[
								{ what: "{{Price}}", filler: formatCreditPrice(extraCredits.creditPriceEur) }
							]}
						/>
					)}
					trackingId="extra-credits-used"
				/>
			</li>
		</ul>
	);
};
UsageCards.displayName = "UsageCards";

type UsageCardProps = {
	name: string;
	amount?: string;
	used: string;
	note?: ReactNode;
	trackingId: string;
	usedPercentage?: number;
};

const UsageCard = (props: UsageCardProps): ReactElement => {
	const {
		name,
		amount,
		used,
		note,
		trackingId,
		usedPercentage
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.card}
		>
			<div className={styles.header}>
				<h3
					{...setTrackingId("title")}
					className={styles.title}
					title={name}
				>
					{name}
				</h3>
				{isNotNil(amount) && (
					<span
						{...setTrackingId("amount")}
						className={styles.amount}
						title={amount}
					>
						{amount}
					</span>
				)}
			</div>
			<p
				{...setTrackingId("subtitle")}
				className={styles.subtitle}
			>
				{localize("_CreditsUsed")}
			</p>
			<div className={styles.bottom}>
				<p
					{...setTrackingId("used")}
					className={styles.used}
					title={used}
				>
					{used}
				</p>
				{isNotNil(usedPercentage) && (
					<ProgressBar
						value={usedPercentage}
						ariaLabel={name}
						trackingId="progress"
					/>
				)}
				{isNotNil(note) && (
					<p
						{...setTrackingId("note")}
						className={styles.note}
					>
						{note}
					</p>
				)}
			</div>
		</div>
	);
};
UsageCard.displayName = "UsageCard";

function formatUsed(
	formatNumber: NumberFormatter,
	credits: CreditBucketModel
): string {
	return `${formatNumber(credits.used)} / ${formatNumber(credits.total)}`;
}

function getUsedPercentage(credits: CreditBucketModel): number {
	if (credits.total <= 0) {
		return 0;
	}

	return clamp(
		(credits.used / credits.total) * 100,
		0,
		100
	);
}

function getKellyHelpsSignInUrl(
	kellyHelpsUrl: string,
	kellyTenantId: Guid
): string {
	const base: string = kellyHelpsUrl.endsWith("/")
		? kellyHelpsUrl
		: `${kellyHelpsUrl}/`;

	const url = new URL("signin", base);

	url.searchParams
		.set("tenant", kellyTenantId);

	return url.toString();
}

const currentCulture: string = getCurrentCulture();
